import { useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth.jsx';

const SCRIPT_ID = 'google-identity-services';
const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

function loadGis() {
  if (window.google?.accounts?.id) return Promise.resolve();
  return new Promise((resolve, reject) => {
    let el = document.getElementById(SCRIPT_ID);
    if (!el) {
      el = document.createElement('script');
      el.id = SCRIPT_ID;
      el.src = SCRIPT_SRC;
      el.async = true;
      el.defer = true;
      document.head.appendChild(el);
    }
    el.addEventListener('load', resolve, { once: true });
    el.addEventListener('error', () => reject(new Error('Google script failed')), { once: true });
    if (window.google?.accounts?.id) resolve();
  });
}

/**
 * Google sign-in button.
 *
 * Renders nothing at all when GOOGLE_CLIENT_ID is unset on the server — the
 * server advertises that via /api/auth/config, and email/password auth carries
 * on working normally. That's the documented "Google disabled" state, not a
 * broken one.
 *
 * `onCredential` replaces signing in with something else to do with Google's
 * answer — adding Google to the signed-in account. `divider` is off when the
 * button sits among others that share one (SignInButtons).
 */
export function GoogleButton({ role, onSuccess, onError, onCredential, divider = true }) {
  const { googleClientId, googleEnabled, loginWithGoogle } = useAuth();
  const ref = useRef(null);

  useEffect(() => {
    if (!googleEnabled || !ref.current) return;
    let cancelled = false;

    loadGis()
      .then(() => {
        if (cancelled || !ref.current) return;
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: async ({ credential }) => {
            try {
              const user = onCredential ? await onCredential(credential) : await loginWithGoogle(credential, role);
              onSuccess?.(user);
            } catch (err) {
              onError?.(err.message ?? 'Google sign-in failed');
            }
          },
        });
        window.google.accounts.id.renderButton(ref.current, {
          theme: 'outline',
          size: 'large',
          width: 320,
          text: 'continue_with',
          shape: 'rectangular',
        });
      })
      .catch(() => onError?.('Could not load Google sign-in'));

    return () => {
      cancelled = true;
    };
  }, [googleEnabled, googleClientId, role, loginWithGoogle, onSuccess, onError, onCredential]);

  if (!googleEnabled) return null;
  if (!divider) return <div ref={ref} className="flex justify-center" />;

  return (
    <div className="space-y-3">
      <div ref={ref} className="flex justify-center" />
      <OrDivider />
    </div>
  );
}

export function OrDivider() {
  return (
    <div className="flex items-center gap-3 text-2xs uppercase tracking-wider text-ink-faint">
      <span className="h-px flex-1 bg-rule" />
      or
      <span className="h-px flex-1 bg-rule" />
    </div>
  );
}
